import { DEFAULT_Y_AXIS_KEY } from "../constants";
import { LinearGradient } from "@visx/gradient";
import { AreaClosed, LinePath } from "@visx/shape";
import { memo, useId, useMemo } from "react";
import ChartHelpers from "helpers/ChartHelpers";
import type { Series } from "../AreaChartProvider";
import useAreaChartActions from "../AreaChartProvider/useAreaChartActions";
import useXAxisActions from "../XAxisProvider/useXAxisActions";
import useYAxisActions from "../YAxisProvider/useYAxisActions";

interface AreaProps<T> extends Series<T> {}

function Area<T>(props: AreaProps<T>) {
  const { data, yAxisId = DEFAULT_Y_AXIS_KEY, color, curveType = "linear", connectNulls } = props;

  const id = useId();
  const { xAccessor, yAccessor } = useAreaChartActions();
  const { getYScale } = useYAxisActions();
  const { getXScale } = useXAxisActions();

  const x = getXScale();
  const y = getYScale(yAxisId);

  const gradientId = `gradient-${id}`;

  const curve = useMemo(() => {
    return ChartHelpers.getCurveFactory(curveType);
  }, [curveType]);

  const defined = useMemo(() => {
    return (d: unknown) => connectNulls || yAccessor(d) !== null;
  }, [connectNulls, yAccessor]);

  const xAccessorFn = useMemo(() => {
    return (d: T) => x(xAccessor(d)) ?? 0;
  }, [x, xAccessor]);

  const yAccessorFn = useMemo(() => {
    return (d: T) => y(yAccessor(d)) ?? 0;
  }, [y, yAccessor]);

  if (!y || !x) return null;

  const singleDataPoint = data.length === 1;

  return (
    <g>
      <LinearGradient id={gradientId} from={color} to={color} toOpacity={0} fromOpacity={0.2} />
      <LinePath
        data={data}
        defined={defined}
        x={xAccessorFn}
        y={yAccessorFn}
        stroke={color}
        strokeWidth={singleDataPoint ? 8 : 1}
        curve={curve}
      />
      <AreaClosed
        data={data}
        defined={defined}
        x={xAccessorFn}
        y={yAccessorFn}
        yScale={y ?? 0}
        stroke={`url(#${gradientId})`}
        fill={`url(#${gradientId})`}
        curve={curve}
      />
    </g>
  );
}

export default memo(Area);
